/**
 * English Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const en: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.funds': 'Funds',
  'nav.investments': 'Investments',
  'nav.investees': 'Investees',
  'nav.esg': 'ESG Review',
  'nav.compliance': 'Compliance',
  'nav.reports': 'Reports',
  'nav.settings': 'Settings',
  'nav.users': 'Users',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indicators',
  'nav.taxonomy': 'EU Taxonomy',
  'nav.filings': 'Filings',
  'nav.calendar': 'Calendar',
  'nav.submit': 'Submit Data',
  'nav.submissions': 'My Submissions',
  'nav.benchmarks': 'Benchmarks',
  'nav.messages': 'Messages',
  'nav.portfolio': 'Portfolio',
  'nav.performance': 'Fund Performance',
  'nav.esgSummary': 'ESG Summary',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Impact Metrics',
  'nav.sdg': 'SDG Mapping',
  'nav.audit': 'Audit Trail',
  'nav.snapshots': 'Data Snapshots',
  'nav.knowledgeGraph': 'Knowledge Graph',
  'nav.geographic': 'Geographic',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Assistant',
  'nav.tasks': 'Tasks',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatory Monitor',

  // Common Actions
  'action.create': 'Create',
  'action.edit': 'Edit',
  'action.delete': 'Delete',
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.submit': 'Submit',
  'action.approve': 'Approve',
  'action.reject': 'Reject',
  'action.download': 'Download',
  'action.export': 'Export',
  'action.import': 'Import',
  'action.filter': 'Filter',
  'action.search': 'Search',
  'action.refresh': 'Refresh',
  'action.close': 'Close',
  'action.back': 'Back',
  'action.next': 'Next',
  'action.previous': 'Previous',
  'action.viewAll': 'View All',
  'action.viewDetails': 'View Details',
  'action.generate': 'Generate',
  'action.signOut': 'Sign Out',
  'action.addNew': 'Add New',
  'action.invite': 'Invite',

  // Statuses
  'status.draft': 'Draft',
  'status.submitted': 'Submitted',
  'status.validating': 'Validating',
  'status.validated': 'Validated',
  'status.approved': 'Approved',
  'status.rejected': 'Rejected',
  'status.review': 'In Review',
  'status.filed': 'Filed',
  'status.active': 'Active',
  'status.inactive': 'Inactive',
  'status.closed': 'Closed',
  'status.fundraising': 'Fundraising',
  'status.investing': 'Investing',
  'status.monitoring': 'Monitoring',
  'status.exiting': 'Exiting',
  'status.harvesting': 'Harvesting',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Due Diligence',
  'status.disbursed': 'Disbursed',
  'status.restructured': 'Restructured',
  'status.matured': 'Matured',
  'status.written_off': 'Written Off',
  'status.exited': 'Exited',
  'status.overdue': 'Overdue',
  'status.compliant': 'Compliant',
  'status.pending': 'Pending',
  'status.incomplete': 'Incomplete',
  'status.open': 'Open',
  'status.in_progress': 'In Progress',
  'status.inProgress': 'In Progress',
  'status.completed': 'Completed',
  'status.cancelled': 'Cancelled',
  'status.accepted': 'Accepted',
  'status.failed': 'Failed',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portfolio Overview',
  'dashboard.complianceStatus': 'Compliance Status',
  'dashboard.collectionProgress': 'Collection Progress',
  'dashboard.activityFeed': 'Activity',
  'dashboard.quickActions': 'Quick Actions',
  'dashboard.investmentPipeline': 'Investment Pipeline',
  'dashboard.totalFunds': 'Total Funds',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Active Investments',
  'dashboard.totalInvestees': 'Investees',
  'dashboard.recentActivity': 'Recent Activity',
  'dashboard.viewBoard': 'View Board',
  'dashboard.viewAll': 'View All',
  'dashboard.noPipelineData': 'No pipeline data available',
  'dashboard.noCollectionData': 'No collection data',
  'dashboard.noComplianceData': 'No compliance data available',
  'dashboard.noRecentActivity': 'No recent activity',

  // Quick Action Labels
  'action.createFund': 'Create Fund',
  'action.addInvestment': 'Add Investment',
  'action.inviteInvestee': 'Invite Investee',
  'action.generateReport': 'Generate Report',
  'action.sfdrFiling': 'SFDR Filing',
  'action.sendDataRequest': 'Send Data Request',

  // Page Titles
  'page.investments': 'Investments',
  'page.funds': 'Funds',
  'page.esg': 'ESG Data Management',
  'page.compliance': 'Compliance',
  'page.reports': 'Reports',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Total Committed',
  'lp.capitalCalled': 'Capital Called',
  'lp.distributed': 'Distributed',
  'lp.netPosition': 'Net Position',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fund Performance',
  'lp.esgSummary': 'ESG Summary',
  'lp.reportLibrary': 'Report Library',
  'lp.taxonomyAligned': 'Taxonomy Aligned',
  'lp.paiCoverage': 'PAI Coverage',
  'lp.portfolioPerformance': 'LP Portfolio Performance',

  // Fund Labels
  'fund.name': 'Fund Name',
  'fund.code': 'Fund Code',
  'fund.vintage': 'Vintage Year',
  'fund.size': 'Fund Size',
  'fund.targetSize': 'Target Size',
  'fund.currency': 'Currency',
  'fund.strategy': 'Strategy',
  'fund.status': 'Status',
  'fund.sfdrCategory': 'SFDR Category',
  'fund.geographyFocus': 'Geography Focus',
  'fund.sectorFocus': 'Sector Focus',

  // Form Labels
  'form.name': 'Name',
  'form.email': 'Email',
  'form.country': 'Country',
  'form.amount': 'Amount',
  'form.currency': 'Currency',
  'form.date': 'Date',
  'form.description': 'Description',
  'form.notes': 'Notes',
  'form.type': 'Type',
  'form.period': 'Period',
  'form.year': 'Year',
  'form.required': 'Required',
  'form.optional': 'Optional',

  // Table Headers
  'table.fund': 'Fund',
  'table.commitment': 'Commitment',
  'table.called': 'Called',
  'table.distributed': 'Distributed',
  'table.tvpi': 'TVPI',
  'table.status': 'Status',
  'table.regulation': 'Regulation',
  'table.filingType': 'Filing Type',
  'table.reportingPeriod': 'Period',
  'table.createdAt': 'Created',
  'table.actions': 'Actions',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU Taxonomy',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Data Completeness',
  'compliance.nextDeadline': 'Next Deadline',

  // Reports
  'report.quarterly': 'Quarterly Report',
  'report.annual': 'Annual Report',
  'report.paiStatement': 'PAI Statement',
  'report.taxonomyReport': 'Taxonomy Report',
  'report.generatedAt': 'Generated',
  'report.version': 'Version',

  // General
  'general.loading': 'Loading...',
  'general.noData': 'No data available',
  'general.error': 'An error occurred',
  'general.retry': 'Retry',
  'general.total': 'Total',
  'general.page': 'Page',
  'general.of': 'of',
  'general.showing': 'Showing',
  'general.results': 'results',
  'general.language': 'Language',
  'general.english': 'English',
  'general.french': 'French',

  // Tour
  'tour.takeTour': 'Take a Tour',
  'tour.skip': 'Skip',
  'tour.next': 'Next',
  'tour.prev': 'Previous',
  'tour.finish': 'Finish',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigation',
  'tour.sidebarContent': 'Use the sidebar to navigate between portfolio management, ESG data collection, compliance, reports, and intelligence tools.',
  'tour.kpisTitle': 'Portfolio Overview',
  'tour.kpisContent': 'These cards show your portfolio at a glance — total funds, AUM, active investments, and investee count.',
  'tour.actionsTitle': 'Quick Actions',
  'tour.actionsContent': 'Create funds, add investments, invite investees, and generate reports with one click.',
  'tour.languageTitle': 'Language',
  'tour.languageContent': 'Switch between 24 official EU languages. The entire interface translates instantly.',
  'tour.aiTitle': 'AI Assistant',
  'tour.aiContent': 'Ask questions about your portfolio anytime. The AI assistant has full context about your funds and investments.',
  'action.editFund': 'Edit Fund',
  'action.addInvestee': 'Add Investee',
  'action.editInvestee': 'Edit Investee',
  'action.createTemplate': 'Create Template',
  'action.createSubmission': 'Create Submission',
  'action.createFiling': 'Create Filing',
  'action.inviteUser': 'Invite User',
  'action.sendInvitation': 'Send Invitation',
  'action.saveChanges': 'Save Changes',
  'form.fundName': 'Fund Name',
  'form.fundCode': 'Fund Code',
  'form.sfdrCategory': 'SFDR Category',
  'form.baseCurrency': 'Base Currency',
  'form.targetSize': 'Target Size',
  'form.vintageYear': 'Vintage Year',
  'form.geographyFocus': 'Geography Focus',
  'form.sectorFocus': 'Sector Focus',
  'form.investmentCode': 'Investment Code',
  'form.instrumentType': 'Instrument Type',
  'form.maturityDate': 'Maturity Date',
  'form.sector': 'Sector',
  'form.countryCode': 'Country Code',
  'form.entityType': 'Entity Type',
  'form.region': 'Region',
  'form.staffCount': 'Staff Count',
  'form.borrowers': 'Borrowers',
  'form.contactName': 'Contact Name',
  'form.contactEmail': 'Contact Email',
  'form.templateName': 'Template Name',
  'form.templateType': 'Template Type',
  'form.indicators': 'Indicators',
  'form.reportingPeriod': 'Reporting Period',
  'form.reportingYear': 'Reporting Year',
  'form.regulation': 'Regulation',
  'form.filingType': 'Filing Type',
  'form.reportType': 'Report Type',
  'form.emailAddress': 'Email Address',
  'form.role': 'Role',
  'form.selectFund': 'Select fund...',
  'form.selectInvestee': 'Select investee...',
  'table.code': 'Code',
  'table.investee': 'Investee',
  'table.instrument': 'Instrument',
  'table.amount': 'Amount',
  'table.currency': 'Currency',
  'table.maturity': 'Maturity',
  'table.name': 'Name',
  'table.country': 'Country',
  'table.type': 'Type',
  'table.period': 'Period',
  'table.completion': 'Completion',
  'table.submitted': 'Submitted',
  'table.version': 'Version',
  'table.indicators': 'Indicators',
  'table.dueDate': 'Due Date',
  'table.generated': 'Generated',
  'table.email': 'Email',
  'table.role': 'Role',
  'error.createFailed': 'Failed to create. Please try again.',
  'error.updateFailed': 'Failed to update. Please try again.',
  'error.loadFailed': 'Failed to load data.',

  // Sidebar Sections
  'sections.portfolio': 'PORTFOLIO',
  'sections.dataCollection': 'DATA COLLECTION',
  'sections.intelligence': 'INTELLIGENCE',
  'sections.ecosystem': 'ECOSYSTEM',
  'sections.admin': 'ADMIN',
  'sections.other': 'OTHER',

  // Page Titles
  'pages.messagesTitle': 'Messages',
  'pages.taxonomyTitle': 'EU Taxonomy',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Knowledge Graph',
  'pages.benchmarksTitle': 'Benchmarks',
  'pages.mcpTitle': 'MCP Server Integration',
  'pages.tasksTitle': 'Tasks',
  'pages.submitDataTitle': 'Submit ESG Data',

  // Empty States
  'empty.benchmarksComingSoon': 'Benchmarks Coming Soon',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'EU Taxonomy',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Other',
  'labels.critical': 'Critical',
  'labels.high': 'High',
  'labels.medium': 'Medium',
  'labels.low': 'Low',
};
